<template>
  <v-text-field v-model="hhmm" @blur="onBlur" @keydown="onKeyDown" :label="label" :placeholder="placeholder"></v-text-field>
</template>

<script>
function cloneDate(date) {
  return date && date.getTime ? new Date(date.getTime()) : new Date();
}
export default {
  name: 'TimeField',
  props: {
    label: String,
    value: String, // {String} v-model as Date Iso String
    placeholder: String,
    defaultDate: Date,
  },
  data() {
    return {
      date: cloneDate(this.defaultDate),
      hhmm: '', //this.dateIsoStringToHHMM(this.value)
    };
  },
  mounted() {
    this.setDateAndHHMM(this.value);
  },
  methods: {
    setDateAndHHMM(dateIsoString) {
      let parsedDate = this.parseDateIsoString(dateIsoString);
      this.hhmm = parsedDate.hhmm;
      this.date = parsedDate.date;
    },
    onKeyDown(e) {
      const validKeys = ['ArrowLeft', 'ArrowRight', 'Tab', 'Backspace', 'Delete'];
      const { value, selectionStart, selectionEnd } = e.target;
      const hasSelection = selectionStart !== selectionEnd;

      // Allow backspacing through the colon
      if (e.key === 'Backspace' && selectionStart === 3 && value[2] === ':' && !hasSelection) {
        e.preventDefault();
        const newValue = value.substring(0, selectionStart - 1) + value.substring(selectionEnd);
        this.hhmm = this.ensureColonPosition(newValue);

        this.$nextTick(() => {
          e.target.selectionStart = e.target.selectionEnd = selectionStart - 1;
        });
        return;
      }

      // Prevent removing or overwriting the colon directly
      if ((e.key === 'Backspace' && value[selectionStart - 1] === ':') || (e.key === 'Delete' && value[selectionStart] === ':')) {
        e.preventDefault();
        return;
      }

      if (validKeys.includes(e.key)) {
        return;
      }

      // Prevent default if max length reached and no selection to replace
      if (value.length >= 5 && !validKeys.includes(e.key) && !hasSelection) {
        e.preventDefault();
        return;
      }

      if (!'0123456789'.includes(e.key) && !hasSelection) {
        e.preventDefault();
        return;
      }

      if ('0123456789'.includes(e.key)) {
        let newValue;
        if (hasSelection) {
          newValue = value.substring(0, selectionStart) + e.key + value.substring(selectionEnd);
        } else if (value.length === 2 && selectionStart === 2) {
          // Inserting colon automatically after 2 digits
          newValue = `${value}:` + e.key;
        } else {
          newValue = value.substring(0, selectionStart) + e.key + value.substring(selectionStart);
        }

        newValue = this.ensureColonPosition(newValue);
        this.hhmm = newValue;
        e.preventDefault();

        this.$nextTick(() => {
          const newPos = selectionStart + (newValue[selectionStart] === ':' ? 2 : 1);
          e.target.selectionStart = e.target.selectionEnd = newPos;
        });
      }
    },

    /**
     * Helper method to ensure the colon is correctly positioned
     * @param {string} value
     * @return {*|string}
     */
    ensureColonPosition(value) {
      const numbersOnly = value.replace(/[^0-9]/g, '');
      if (numbersOnly.length <= 2) return numbersOnly;
      return numbersOnly.substring(0, 2) + ':' + numbersOnly.substring(2, 4);
    },

    /**
     * Ensures that the blank fields are not empty and always have a '0'.
     * Specifically when 'HH' are missing a number it adds a '0' to the left and when 'mm' is missing a number it adds
     * a '0' to the right.
     * It's needed because having blank fields doesn't let you save correctly.
     * Emits the input event at the end with the hhmm.
     */
    onBlur() {
      if (!this.hhmm || this.hhmm.length === 0) {
        console.debug('ignore when the user clicks outside without having entered a time (previously set to 00:00');
      } else {
        // Ensure the value has a colon and split into parts
        let parts = this.hhmm.split(':');
        let hours = parts[0] || '00';
        let minutes = parts[1] || '00';

        let date = cloneDate(this.defaultDate);
        date.setHours(Number(hours)); // without cloning, hours and minutes where set to the defaultDate object which might be the context date
        date.setMinutes(Number(minutes));
        let correctedIsoString = date.toISOString();
        console.debug(`emit this.date=${this.defaultDate} correctedIsoString=${correctedIsoString} `);
        this.$emit('input', correctedIsoString);
      }
    },

    dateToHHMM(date) {
      return `${date.getHours()}`.padStart(2, '0') + ':' + `${date.getMinutes()}`.padStart(2, '0');
    },
    parseDateIsoString(dateIsoString) {
      let time = Date.parse(dateIsoString);
      if (isNaN(time)) {
        return { hhmm: '', date: cloneDate(this.defaultDate) };
      } else {
        let date = new Date(time);
        return { hhmm: this.dateToHHMM(date), date };
      }
    },
  },
  watch: {
    value(newValue) {
      this.setDateAndHHMM(newValue);
    },
  },
};
</script>
